import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../lib/auth.js'
import { type TaskListPage, type TaskListItem } from '../manager/useTasks.js'

export interface MyTask extends TaskListItem {
  readonly description?: string
  readonly dependsOn?: {
    readonly id: string
    readonly reference: string
    readonly title: string
    readonly status: string
  } | null
}

/**
 * Active work only. Without this the list also carries approved tasks, and with a
 * page size of 25 they push real work off the first page. Submitted work moves to
 * My progress.
 */
const MY_DAY_STATUSES = 'ASSIGNED,IN_PROGRESS,RETURNED'

export function useMyTasks() {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['tasks', MY_DAY_STATUSES],
    queryFn: () =>
      api.get<TaskListPage>(`/api/tasks?statuses=${encodeURIComponent(MY_DAY_STATUSES)}`),
    retry: false,
  })
}

export function useStartTask() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) => api.post(`/api/tasks/${taskId}/start`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useSubmitTask() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      payload: { taskId: string; channel: 'PLATFORM' | 'EXTERNAL'; note?: string },
    ) => {
      const { taskId, ...body } = payload
      return api.post(`/api/tasks/${taskId}/submit`, body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useBlockTask() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { taskId: string; note: string }) => {
      const { taskId, note } = payload
      return api.post(`/api/tasks/${taskId}/block`, { note })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}
